import FormInputField from '@/components/ui/FormInputField';
import { useUpdateCurrentOrderCustomerMutation } from '@/hooks/mutations/use-update-current-order-customer';
import useCustomerFormDefaultValues from '@/hooks/use-customer-form-default-values';
import { customerFormSchema } from '@/validation/customer-schema';
import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';

type CustomerFormProps = { onDone: () => void };

export default function CustomerForm({ onDone }: CustomerFormProps) {
  const queryClient = useQueryClient();
  const defaultValues = useCustomerFormDefaultValues();
  const { mutate } = useUpdateCurrentOrderCustomerMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] }).then(() => onDone());
    },
  });

  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: customerFormSchema,
    },
    onSubmit: async ({ value }) => mutate(value),
  });

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="space-y-6 mb-6">
        <div className="flex space-x-4">
          <FormInputField className="flex-1 min-w-0" formApi={form} name="firstName" label="Förnamn" required />
          <FormInputField className="flex-1 min-w-0" formApi={form} name="lastName" label="Efternamn" required />
        </div>
        <FormInputField formApi={form} name="street" label="Gatuadress" required />
        <div className="flex space-x-4">
          <FormInputField className="flex-1 min-w-0" formApi={form} name="postalCode" label="Postnummer" required />
          <FormInputField className="flex-1 min-w-0" formApi={form} name="city" label="Postort" required />
        </div>
        <div className="flex flex-col space-y-6 xs:flex-row xs:space-x-4 xs:space-y-0 sm:flex-col sm:space-x-0 sm:space-y-6 md:flex-row md:space-x-4 md:space-y-0">
          <FormInputField className="flex-1 min-w-0" formApi={form} name="email" type="email" label="E-post" required />
          <FormInputField className="flex-1 min-w-0" formApi={form} name="mobileNumber" label="Mobilnummer" required />
        </div>
      </div>

      <div className="flex gap-2">
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <button
              type="submit"
              disabled={!canSubmit}
              className="btn w-full bg-violet-500 text-gray-100 hover:bg-violet-700 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white mb-4"
            >
              {isSubmitting ? 'Uppdaterar...' : 'Fortsätt'}
            </button>
          )}
        />
      </div>
    </form>
  );
}
